import React from "react";
import { useSearchParams } from "react-router-dom";

import { composeFunnelbackSortParam } from "./formatSortOrder";
import { detectCase } from "../../utils/detectCase";
import { joinClassNames } from "../../utils/joinClassNames";
import { useSearchQuestion } from "../../context/DataContext";
import { ReactComponent as IconChevronDown } from "../../assets/icons/chevron-down.svg";
import { SortValues } from "../../types/context";

import styles from "./SearchResultsTable.module.css";
import headerStyles from "./HeaderRow.module.css";

export type HeaderCellProps = {
    columnKey: string;
    label: string;
    cellClassName?: string;
};

export const HeaderCell = ({
    columnKey,
    label,
    cellClassName,
}: HeaderCellProps) => {
    if (
        detectCase(columnKey) !== "camelcase" &&
        detectCase(columnKey) !== "lowercase"
    ) {
        throw new Error("columnKey value must use camelCase");
    }

    const sortParamKey = "sort";
    const [searchParams, setSearchParams] = useSearchParams();
    const { setSortOrder, sortOrder } = useSearchQuestion();

    const isSortColumn = sortOrder.field === columnKey;
    const hasSortDirection = sortOrder.direction !== "none";

    type OptionalHeaderProps = {
        "aria-sort"?: SortValues;
    };
    const optionalHeaderProps: OptionalHeaderProps = {};
    if (isSortColumn && hasSortDirection) {
        optionalHeaderProps["aria-sort"] = sortOrder.direction;
    }

    type OptionalButtonProps = {
        "aria-pressed"?: boolean;
    };
    const optionalButtonProps: OptionalButtonProps = {};
    if (isSortColumn && hasSortDirection) {
        optionalButtonProps["aria-pressed"] = true;
    }

    const handleSortClick = () => {
        const newSearchParams = new URLSearchParams(searchParams);
        const newColumnKey =
            !isSortColumn ||
            (isSortColumn && sortOrder.direction === "ascending")
                ? columnKey
                : "";
        const newDirection = !isSortColumn
            ? "ascending"
            : isSortColumn && sortOrder.direction === "ascending"
            ? "descending"
            : "none";
        const newSortParamValue = composeFunnelbackSortParam({
            field: columnKey,
            direction: newDirection,
        });
        // Compose new front-end URL search params
        if (newColumnKey && newDirection !== "none" && newSortParamValue) {
            newSearchParams.set(sortParamKey, newSortParamValue);
        } else {
            newSearchParams.delete(sortParamKey);
        }
        // Fetch new data from search endpoint
        setSortOrder(newColumnKey, newDirection);
        // Set new front-end URL search params
        setSearchParams(newSearchParams);
    };

    const maybeSortIcon =
        isSortColumn && hasSortDirection ? (
            <IconChevronDown
                className={joinClassNames([
                    headerStyles.sortIcon,
                    headerStyles[sortOrder.direction] || "",
                ])}
            />
        ) : null;

    return (
        <th
            {...optionalHeaderProps}
            scope="col"
            className={joinClassNames([
                styles.cell,
                headerStyles.header,
                cellClassName
                    ? headerStyles?.[cellClassName]
                    : headerStyles?.[columnKey],
            ])}
        >
            <button
                type="button"
                {...optionalButtonProps}
                className={headerStyles.sortButton}
                onClick={handleSortClick}
            >
                <span>{label.trim()}</span>
                {maybeSortIcon}
            </button>
        </th>
    );
};

export default HeaderCell;
